/**
 * Publish flow modal: 1) 绑定域名 → 2) DNS 检测 → 3) 触发发布。
 */
import {useCallback, useEffect, useState} from 'react';
import {domainsApi, sitesApi} from '../lib/api-resources';
import type {
  Domain,
  DomainCreate,
  DomainCheckResult,
  PublishCreateRequest,
  Site,
} from '../lib/types';
import {friendlyError} from '../lib/error-labels';
import {DNS_STATUS_LABELS, labelOf} from '../lib/ui-labels';
import Modal from './Modal';

interface PublishModalProps {
  site: Site;
  onClose: () => void;
  /** Called after a successful publish so the parent can refresh. */
  onPublished?: (jobId: string) => void;
}

type Step = 'BIND_DOMAIN' | 'CHECK_DNS' | 'TRIGGER' | 'RUNNING';

const STEP_LABEL: Record<Step, string> = {
  BIND_DOMAIN: '1. 绑定域名',
  CHECK_DNS: '2. 检查 DNS',
  TRIGGER: '3. 发布',
  RUNNING: '正在发布…',
};

const FRIENDLY_STAGE: Record<string, string> = {
  // Backend pipeline stages; mapped to operator-friendly Chinese.
  CHECK_SITE: '正在验证站点',
  CHECK_SERVER: '正在验证服务器',
  CHECK_DNS: '正在检查 DNS',
  DETECT_PHP_FPM: '正在识别 PHP-FPM',
  RENDER_HTTP_NGINX: '正在生成 HTTP Nginx 配置',
  VALIDATE: '正在校验配置',
  ENABLE_HTTP: '正在启用 HTTP',
  VERIFY_HTTP: '正在验证 HTTP 访问',
  ISSUE_SSL: '正在签发 SSL 证书',
  RENDER_HTTPS_NGINX: '正在生成 HTTPS Nginx 配置',
  ENABLE_HTTPS: '正在启用 HTTPS',
  UPDATE_WP_URL: '正在更新 WordPress 站点地址',
  VERIFY_HTTPS: '正在验证 HTTPS 访问',
  FINALIZE: '正在完成',
};

function friendlyStage(step: string | null | undefined): string {
  if (!step) return '正在准备';
  return FRIENDLY_STAGE[step] ?? `处理中（${step}）`;
}

export default function PublishModal({site, onClose, onPublished}: PublishModalProps) {
  const [step, setStep] = useState<Step>('BIND_DOMAIN');
  const [domains, setDomains] = useState<Domain[]>([]);
  const [creating, setCreating] = useState(false);
  const [checking, setChecking] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [domain, setDomain] = useState<Domain | null>(null);
  const [dnsResult, setDnsResult] = useState<DomainCheckResult | null>(null);

  const [form, setForm] = useState<DomainCreate>({
    canonical_domain: site.domain ?? '',
    www_domain: site.domain ? `www.${site.domain}` : '',
    www_enabled: true,
    apex_canonical: true,
  });

  const loadDomains = useCallback(async () => {
    try {
      const res = await domainsApi.list(site.id);
      setDomains(res.items);
      if (res.items.length > 0 && !domain) {
        setDomain(res.items[0]);
        setForm({
          canonical_domain: res.items[0].canonical_domain,
          www_domain: res.items[0].www_domain ?? '',
          www_enabled: res.items[0].www_enabled,
          apex_canonical: res.items[0].apex_canonical,
        });
        setStep('CHECK_DNS');
      }
    } catch (e: any) {
      setError(friendlyError(e?.code, e?.message).headline);
    }
  }, [site.id, domain]);

  useEffect(() => {
    void loadDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreateDomain(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setInfo(null);
    try {
      const body: DomainCreate = {
        canonical_domain: form.canonical_domain.trim().toLowerCase(),
        www_domain: form.www_domain?.trim() ? form.www_domain.trim().toLowerCase() : null,
        www_enabled: form.www_enabled,
        apex_canonical: form.apex_canonical,
      };
      const d = await domainsApi.create(site.id, body);
      setDomain(d);
      setInfo(`已绑定域名：${d.canonical_domain}`);
      setStep('CHECK_DNS');
      void loadDomains();
    } catch (e: any) {
      setError(friendlyError(e?.code, e?.message).headline);
    } finally {
      setCreating(false);
    }
  }

  async function handleCheckDns() {
    if (!domain) return;
    setChecking(true);
    setError(null);
    setInfo(null);
    try {
      const r = await domainsApi.checkDns(site.id, domain.id);
      setDnsResult(r);
      const fresh = await domainsApi.get(domain.id);
      setDomain(fresh);
      if (r.matches_target) {
        setInfo(`DNS 正常：${r.canonical_domain} → ${r.target_server}`);
        setStep('TRIGGER');
      } else {
        setError(
          `域名 ${r.canonical_domain} 当前未解析到此服务器（解析到：${r.resolved_ipv4.join(', ') || 'NXDOMAIN'}）。请到域名服务商添加 A 记录。`
        );
      }
    } catch (e: any) {
      setError(friendlyError(e?.code, e?.message).headline);
    } finally {
      setChecking(false);
    }
  }

  async function handleTriggerPublish(opts: {skipDnsCheck: boolean}) {
    setPublishing(true);
    setError(null);
    setInfo(null);
    try {
      const body: PublishCreateRequest = {
        certbot_staging: true,
        skip_dns_check: opts.skipDnsCheck,
      };
      const resp = await sitesApi.publish(site.id, body);
      setStep('RUNNING');
      onPublished?.(resp.deployment_job_id);
    } catch (e: any) {
      setError(friendlyError(e?.code, e?.message).headline);
    } finally {
      setPublishing(false);
    }
  }

  return (
    <Modal
      open
      title={`发布「${site.name}」`}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            关闭
          </button>
        </>
      }
    >
      <div className="publish-modal">
        <div className="step-tabs">
          {(['BIND_DOMAIN', 'CHECK_DNS', 'TRIGGER', 'RUNNING'] as Step[]).map((s) => (
            <span key={s} className={`step-tab ${step === s ? 'active' : ''}`}>
              {STEP_LABEL[s]}
            </span>
          ))}
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {info && <div className="alert alert-ok">{info}</div>}

        {step === 'BIND_DOMAIN' && (
          <form onSubmit={handleCreateDomain} className="form-grid">
            <label>
              <span>主域名 *</span>
              <input
                type="text"
                value={form.canonical_domain}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    canonical_domain: e.target.value,
                    www_domain: f.www_domain || `www.${e.target.value}`,
                  }))
                }
                placeholder="example.com"
                required
                pattern="[a-z0-9.-]+\.[a-z]{2,}"
              />
            </label>
            <label>
              <span>www 子域名（可选）</span>
              <input
                type="text"
                value={form.www_domain ?? ''}
                onChange={(e) => setForm((f) => ({...f, www_domain: e.target.value}))}
                placeholder="www.example.com"
                pattern="[a-z0-9.-]+\.[a-z]{2,}"
              />
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={form.www_enabled ?? true}
                onChange={(e) => setForm((f) => ({...f, www_enabled: e.target.checked}))}
              />
              <span>启用 www 重定向（主域名为规范地址）</span>
            </label>
            <div className="form-row">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={creating || !form.canonical_domain}
              >
                {creating ? '绑定中…' : '绑定域名'}
              </button>
            </div>
            {domains.length > 0 && (
              <div className="form-row">
                <span className="muted small">
                  已绑定：{' '}
                  {domains.map((d) => d.canonical_domain).join(', ')}
                </span>
              </div>
            )}
          </form>
        )}

        {step === 'CHECK_DNS' && domain && (
          <div className="dns-check">
            <p>
              已绑定：<code>{domain.canonical_domain}</code>
              {domain.www_domain && (
                <> / <code>{domain.www_domain}</code></>
              )}
            </p>
            <p className="muted small">
              DNS 状态：<strong>{labelOf(DNS_STATUS_LABELS, domain.dns_status, domain.dns_status)}</strong>
              {domain.dns_checked_at && (
                <> （检测时间：{domain.dns_checked_at.slice(0, 19).replace('T', ' ')}）</>
              )}
            </p>
            <div className="form-row">
              <button
                className="btn btn-primary"
                onClick={handleCheckDns}
                disabled={checking}
              >
                {checking ? '检查中…' : '重新检查 DNS'}
              </button>
              {dnsResult && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setStep('TRIGGER')}
                >
                  跳过 DNS 直接发布
                </button>
              )}
            </div>
            {dnsResult && (
              <div className="dns-detail">
                <div>
                  <span className="muted small">解析到的 IPv4：</span>
                  <span>{dnsResult.resolved_ipv4.join(', ') || '—'}</span>
                </div>
                <div>
                  <span className="muted small">解析到的 IPv6：</span>
                  <span>{dnsResult.resolved_ipv6.join(', ') || '—'}</span>
                </div>
                <div>
                  <span className="muted small">目标服务器：</span>
                  <span>{dnsResult.target_server}</span>
                </div>
                <div>
                  <span className="muted small">是否匹配：</span>
                  <span className={dnsResult.matches_target ? 'ok' : 'err'}>
                    {dnsResult.matches_target ? '✓ 是' : '✗ 否'}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'TRIGGER' && domain && (
          <div className="trigger-publish">
            <p>
              即将把 <strong>{site.name}</strong> 发布到{' '}
              <code>{dnsResult?.matches_target ? 'https://' + domain.canonical_domain : domain.canonical_domain}</code>。
            </p>
            <p className="muted small">
              发布过程会自动完成：验证站点、配置 Nginx、签发 SSL 证书并启用 HTTPS，最后更新 WordPress 站点地址并校验。
            </p>
            <p className="muted small">
              <strong>提示：</strong>默认使用 Let's Encrypt 测试环境，避免触发正式证书频率限制。需要正式证书请在「技术详情」中调整。
            </p>
            <div className="form-row">
              <button
                className="btn btn-primary"
                onClick={() => handleTriggerPublish({skipDnsCheck: false})}
                disabled={publishing}
              >
                {publishing ? '启动中…' : '开始发布'}
              </button>
              {dnsResult && !dnsResult.matches_target && (
                <button
                  className="btn btn-warn"
                  onClick={() => handleTriggerPublish({skipDnsCheck: true})}
                  disabled={publishing}
                  title="仅在你已通过其他方式确认 DNS 解析正确时使用"
                >
                  跳过 DNS 直接发布
                </button>
              )}
            </div>
          </div>
        )}

        {step === 'RUNNING' && (
          <div>
            <p>发布任务已提交，请在打开的部署窗口中查看进度。</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

// Re-export so other modules (e.g. sites page summary card) can show
// the same friendly stage name without re-implementing the lookup.
export {friendlyStage};
